"use client";

import type { Faq } from "@/lib/content/types";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";
import { EASE } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { Panel, PanelHeader } from "@/components/ui/primitives";
import { useContent } from "@/components/shell/ContentContext";

export function FaqSection({ faqs }: { faqs: Faq[] }) {
  const [open, setOpen] = useState(0);

  return (
    <Panel>
      <PanelHeader icon="help" title="Frequently Asked Questions" />
      <div className="flex flex-col items-start gap-[12px] p-[12px]">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.q} className="w-full overflow-hidden rounded-xl bg-elevation-2 p-[20px]">
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                className="flex w-full items-start gap-[8px] text-left"
              >
                <span className="flex-1 text-[15.75px] font-extrabold leading-[1.6] text-fg lg:text-[17.5px]">{f.q}</span>
                <motion.span
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.25, ease: EASE }}
                  className="grid h-[28px] w-[28px] shrink-0 place-items-center"
                >
                  <Icon name="chevron-down" size={28} className="text-fg" />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.32, ease: EASE }}
                    className="w-full overflow-hidden"
                  >
                    <p className="pt-[12px] text-[14px] font-medium leading-[1.6] text-fg-subtle">
                      {f.a.split("traditional currencies").map((part, idx, arr) => (
                        <span key={idx}>
                          {part}
                          {idx < arr.length - 1 && (
                            <Link
                              href="/blog/online-casino-guide"
                              className="text-brand-fg-accent underline underline-offset-2"
                            >
                              traditional currencies
                            </Link>
                          )}
                        </span>
                      ))}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

export function ContentBlock({
  eyebrow,
  headline,
  body,
  kicker,
  stats,
}: {
  eyebrow: string;
  headline: string;
  body: string;
  kicker: string;
  stats: { k: string; v: string }[];
}) {
  const block = { eyebrow, headline, body, kicker, stats };
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, ease: EASE }}
      className="relative flex flex-col items-center justify-center px-[16px] py-[24px] text-center sm:px-[48px] sm:py-[32px]"
    >
      <div className="flex w-full max-w-[1000px] flex-col items-center gap-[12px]">
        <p className="w-full text-[12px] font-bold uppercase leading-[1.6] text-brand-fg-accent">{block.eyebrow}</p>
        <h2 className="w-full text-h4 font-black lg:text-h2">{block.headline}</h2>
        <p className="w-full whitespace-pre-wrap text-[14px] leading-[1.6] text-fg-muted lg:text-[17.5px]">
          {block.body}
        </p>
      </div>
      <p className="mt-[24px] text-h6 font-black lg:text-h5">{block.kicker}</p>

      <div className="mx-auto mt-12 grid w-full max-w-[820px] gap-3 sm:grid-cols-3">
        {block.stats.map((s, i) => (
          <motion.div
            key={s.k}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, ease: EASE, delay: i * 0.08 }}
            className="rounded-lg bg-elevation-1/80 p-6 ring-1 ring-border backdrop-blur-sm"
          >
            <p className="text-h5 font-black text-brand-bold lg:text-h3">{s.k}</p>
            <p className="mt-1 text-[12px] font-medium text-fg-subtle">{s.v}</p>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}
